import { useSeo } from '@/lib/seo';
import Header from '@/components/Header';
import { SeverityChip } from '@/components/SeverityChip';
import { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, RefreshCw, Search, MapPin, Clock, CloudRain, Truck, Route as RouteIcon, X } from 'lucide-react';
import { alertOnRoute } from '@/lib/geo';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

export default function AlertsList() {
  useSeo({ title: 'NZ Highway Alerts & Road Closures', description: 'Browse live New Zealand highway alerts — road closures, crashes, hazards and roadworks by island and highway, updated continuously from NZTA Waka Kotahi.' });
  const [params, setParams] = useSearchParams();
  const [alerts, setAlerts] = useState([]);
  const [highways, setHighways] = useState({ north: [], south: [] });
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const severity = params.get('severity') || '';
  const highwayKey = params.get('highway_key') || '';
  const island = params.get('island') || '';
  const statusFilter = params.get('status') || 'all';
  const sourceFilter = params.get('src') || '';
  const scope = params.get('scope') || 'current';
  const routeId = params.get('route') || '';
  const [route, setRoute] = useState(null);

  useEffect(() => { const t = setTimeout(() => setQDebounced(qInput.trim()), 400); return () => clearTimeout(t); }, [qInput]);

  useEffect(() => {
    if (!routeId) { setRoute(null); return; }
    api.get('/routes').then((r) => setRoute((r.data || []).find((x) => x.id === routeId) || null)).catch(() => setRoute(null));
  }, [routeId]);

  const load = async () => {
    setLoading(true);
    try {
      const p = { scope };
      if (severity) p.severity = severity;
      if (highwayKey) p.highway_key = highwayKey;
      if (island && !highwayKey) p.island = island;
      if (statusFilter && statusFilter !== 'all') p.status_filter = statusFilter;
      if (sourceFilter) p.source = sourceFilter === 'weather' ? 'metservice' : sourceFilter === 'auckland' ? 'at' : sourceFilter === 'wellington' ? 'wcc' : sourceFilter;
      if (qDebounced) p.q = qDebounced;
      const { data } = await api.get('/alerts', { params: p });
      setAlerts(data);
      const hs = await api.get('/highways');
      setHighways({ north: hs.data.north || [], south: hs.data.south || [] });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); /* eslint-disable-line */ }, [severity, highwayKey, island, statusFilter, sourceFilter, scope, qDebounced]);

  const filtered = useMemo(() => {
    if (!route) return alerts;
    const keys = route.highway_keys || [];
    const islands = new Set(keys.map((k) => (k[0] === 'N' ? 'north' : 'south')));
    return alerts.filter((a) => (a.source === 'metservice' ? a.island && islands.has(a.island) : alertOnRoute(a, route)));
  }, [alerts, route]);

  const setFilter = (updates) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([k, v]) => {
      if (v) next.set(k, v); else next.delete(k);
    });
    setParams(next);
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-1">Live feed</div>
            <h1 className="font-display font-black text-3xl md:text-4xl">Alerts</h1>
            <p className="text-zinc-600 mt-1">{filtered.length} events shown · road data from NZTA + MetService weather warnings</p>
            {route && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-sm font-semibold pl-3 pr-1.5 py-1" data-testid="alerts-route-filter">
                <RouteIcon className="w-4 h-4" /> On route: {route.name}
                {route.geometry && route.geometry.length ? <span className="text-[10px] font-bold text-teal-600 bg-white border border-teal-200 rounded-full px-1.5 py-0.5">geo-fenced</span> : null}
                <button onClick={() => setFilter({ route: '' })} className="ml-1 p-1 rounded-full hover:bg-teal-100" data-testid="alerts-route-clear" aria-label="Clear route filter"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
          <button onClick={load} className="btn btn-ghost" data-testid="alerts-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-zinc-500" />
            <SeverityFilter value={severity} onChange={(v) => setFilter({ severity: v })} />
            <select className="input max-w-[150px]" value={sourceFilter} onChange={(e) => setFilter({ src: e.target.value })} data-testid="alerts-filter-source">
              <option value="">All sources</option>
              <option value="road">🛣️ Road events</option>
              <option value="weather">🌦️ Weather</option>
              <option value="auckland">🏙️ Auckland (AT)</option>
              <option value="wellington">🏙️ Wellington (WCC)</option>
              <option value="council">🏙️ All local councils</option>
            </select>
            <select className="input max-w-[160px]" value={island} onChange={(e) => setFilter({ island: e.target.value, highway_key: '' })} data-testid="alerts-filter-island">
              <option value="">All islands</option>
              <option value="north">🌋 North Island</option>
              <option value="south">🏔️ South Island</option>
            </select>
            <select className="input max-w-[240px]" value={highwayKey} onChange={(e) => setFilter({ highway_key: e.target.value })} data-testid="alerts-filter-highway">
              <option value="">All highways</option>
              <optgroup label="🌋 North Island">
                {highways.north.map((h) => <option key={h.key} value={h.key}>{h.highway} · North ({h.count})</option>)}
              </optgroup>
              <optgroup label="🏔️ South Island">
                {highways.south.map((h) => <option key={h.key} value={h.key}>{h.highway} · South ({h.count})</option>)}
              </optgroup>
            </select>
            <select className="input max-w-[160px]" value={statusFilter} onChange={(e) => setFilter({ status: e.target.value })} data-testid="alerts-filter-status">
              <option value="all">All statuses</option>
              <option value="Active">Active</option>
              <option value="Resolved">Resolved</option>
            </select>
            <select className="input max-w-[170px]" value={scope} onChange={(e) => setFilter({ scope: e.target.value })} data-testid="alerts-filter-scope">
              <option value="current">🟢 Happening now</option>
              <option value="upcoming">🗓️ Upcoming / planned</option>
              <option value="all">All (incl. past)</option>
            </select>
            <div className="flex-1 min-w-[220px] relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input className="input" style={{ paddingLeft: '2.75rem' }} placeholder="Search all alerts — location, road, description…" value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="alerts-search" />
            </div>
          </div>
        </div>

        {loading && alerts.length === 0 ? (
          <div className="card">Loading alerts…</div>
        ) : filtered.length === 0 ? (
          <div className="card text-center py-16">
            <MapPin className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
            <h3 className="font-display font-bold text-xl mb-1">No alerts match your filters</h3>
            <p className="text-zinc-500">Try clearing the filters above to see all events.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((a) => <AlertCard key={a.id} a={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function SeverityFilter({ value, onChange }) {
  const options = [
    { v: '', label: 'All' },
    { v: 'red', label: 'Critical' },
    { v: 'orange', label: 'Urgent' },
    { v: 'yellow', label: 'Info' },
  ];
  return (
    <div className="inline-flex bg-zinc-100 rounded-lg p-1" data-testid="alerts-filter-severity">
      {options.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${value === o.v ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-600 hover:text-zinc-900'}`}
          data-testid={`severity-btn-${o.v || 'all'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AlertCard({ a }) {
  const isResolved = (a.status || '').toLowerCase().startsWith('resolved');
  const isWeather = a.source === 'metservice';
  return (
    <Link to={`/alerts/${a.id}`} className={`card card-hover block ${isResolved ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-300' : ''}`} data-testid={`alert-card-${a.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <SeverityChip severity={a.severity} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className="font-display font-bold text-lg">{a.event_type}</span>
            {isWeather && (
              <span className="chip inline-flex items-center gap-1 bg-sky-100 text-sky-700 border border-sky-200 text-xs" data-testid={`alert-weather-${a.id}`}>
                <CloudRain className="w-3 h-3" /> Weather
              </span>
            )}
            {!isWeather && a.cause && a.cause.toLowerCase() !== (a.event_type || '').toLowerCase() && (
              <span className="chip chip-outline-zinc text-xs" data-testid={`alert-cause-${a.id}`}>{a.cause}</span>
            )}
            {a.freight_relevant && (
              <span className="chip inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-200 text-xs" data-testid={`alert-freight-${a.id}`}>
                <Truck className="w-3 h-3" /> Freight
              </span>
            )}
            {a.highway && <span className="chip chip-outline-orange">{a.highway}{a.island ? ` · ${a.island === 'north' ? 'North' : 'South'}` : ''}</span>}
            {isWeather && a.island && <span className="chip chip-outline-orange">{a.island === 'north' ? 'North Island' : 'South Island'}</span>}
            {a.source === 'manual' && <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-teal-600">Manual</span>}
            {a.source && !['nzta', 'metservice', 'manual'].includes(a.source) && a.region && <span className="chip chip-outline-teal">{a.region}</span>}
            {isResolved && <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid={`alert-resolved-badge-${a.id}`}>{isWeather ? 'Lifted' : 'Resolved'}</span>}
          </div>
          <div className="text-sm text-zinc-800 font-medium mb-1">{a.location_area}</div>
          <p className="text-sm text-zinc-600 line-clamp-2">{a.description}</p>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-zinc-500">
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {(a.start_date || a.created_at) ? dayjs(a.start_date || a.created_at).fromNow() : '—'}</span>
            {a.impact && <span>Impact: {a.impact}</span>}
            {isWeather && a.end_date && <span>Until: {dayjs(a.end_date).format('D MMM, h:mma')}</span>}
            {a.expected_resolution && <span>ETR: {a.expected_resolution}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}
